<script>

/**
 * Component: Collection Header
 * ------------------------------------------------------------------------------
 * - Handles JavaScript interaction for the heading area above the PLP.
 * @param {String} [sectionId] - Section's Liquid ID.
 */
import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import classes from '~/helpers/classes'
import { isRobot } from '~/helpers/dom'
import { decodeHTMLEntities, truncateString } from '~/helpers/strings'

export default {
  name: 'CollectionHeader',

  components: {},

  props: {
    descriptionText: {
      type: String,
      default: '',
    },
    sectionId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      carousel: {
        active: false,
        hasNavigation: false,
      },
      elements: {
        container: null,
        copy: null,
        copyContainer: null,
        readMore: null,
        linkList: null,
        linksNext: null,
      },
      selectors: {
        copy: '[data-copy]',
        copyContainer: '[data-copy-container]',
        readMore: '[data-read-more]',
        linkList: '[data-link-list]',
        linksNext: '[data-links-next]',
      },
      readMoreActive: false,
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
      viewportWidth: 'width',
    }),

    /**
     * Returns an accessible label for the carousels 'Next'button.
     * @returns {String}
     */
    carouselNextLabel() {
      return mint.strings.accessibility.carousel.next
    },

    /**
     * Calculate if the description requires a 'Read more' button.
     * @returns {Boolean}
     */
    isReadMore() {

      /**
       * Don't show 'Read more' on desktop or for crawlers.
       */
      if (!this.isMobile || isRobot()) {
        return false
      }

      const descriptionLength = this.descriptionText.length
      const maxLength = mint.collection.descriptionCharacterLimit || 999

      return Boolean(descriptionLength > maxLength)
    },
  },

  watch: {

    /**
     * Watch for viewport width resize.
     */
    viewportWidth() {
      this.setCarouselNextButtonState()

      if (!this.isMobile) {
        return
      }

      this.readMoreActive = false
    },

    /**
     * Watch the state of the 'Read more' button.
     */
    readMoreActive() {
      this.updateDescription()

      this.elements.copyContainer.classList.toggle(
        'is-expanded',
        this.readMoreActive,
      )
    },

    /**
     * Watch when the carousel has navigation (links go beyond the viewport).
     * - Align items back to the left to prevent cutting-off first link.
     */
    'carousel.hasNavigation': {
      handler(newValue, oldValue) {
        newValue
          ? this.elements.linkList.classList.add(classes.active)
          : this.elements.linkList.classList.remove(classes.active)
      },
    },
  },

  mounted() {
    this.setElements()
    this.setEventListeners()
    this.updateDescription()
    this.setCarouselNextButtonState()

    if (!this.isMobile) {
      this.readMoreActive = true
    }
  },

  methods: {

    /**
     * If to display and enable
     */
    enableQuickLinks() {
      return this.elements?.linkList &&
        this.elements.linkList.children.length &&
        mint.collection.enableQuickLinks
    },

    /**
     * Calculate the description string.
     * - Returns full or truncated depending on state.
     * @returns {String}
     */
    getDescription() {
      let description = this.descriptionText
      const maxLength = mint.collection.descriptionCharacterLimit || 999
      const readMore = this.isReadMore

      /**
       * Return the truncated description if the user has toggled state.
       */
      if (readMore && !this.readMoreActive) {
        description = truncateString(this.descriptionText, maxLength)
      }

      return decodeHTMLEntities(description)
    },

    /**
     * Set the state of the mobile carousels 'Next' button.
     */
    setCarouselNextButtonState() {
      if (!this.isMobile || !this.enableQuickLinks()) {
        this.carousel.hasNavigation = false
        return
      }

      /**
       * Combine the width of each individual link and add the total.
       * - Using this over this.$refs.quickLinks.scrollWidth as iOS
       *   sometimes thinks the scrollWidth is less than the viewport.
       */
      const linksWidth = [...this.elements.linkList.children]
        .map((link) => link.offsetWidth || 0)
        .reduce((accumulator, incrementor) => accumulator + incrementor)

      this.carousel.hasNavigation = Boolean(linksWidth > this.viewportWidth)

      this.carousel.hasNavigation
        ? this.elements.linksNext.classList.add(classes.active)
        : this.elements.linksNext.classList.remove(classes.active)
    },

    /**
     * Handle events for when the user has scrolled the quick links carousel.
     * @param {Event} event - Quick Links 'scroll' event.
     */
    handleScrollLinks(event) {
      const target = event.target

      const scrollDistance = target.scrollLeft || 0

      const action = scrollDistance > 50 ? 'remove' : 'add'

      this.elements.linksNext.classList[action](classes.active)
    },

    /**
     * Horizontally scroll so that the last element in the
     * Quick Links carousel becomes visible.
     */
    scrollCarousel() {
      const length = this.elements.linkList.children.length
      const lastQuickLink = this.elements.linkList.children[length - 1]

      lastQuickLink.scrollIntoView({ block: 'end' })
    },

    /**
     * Update the description copy on interaction.
     */
    updateDescription() {
      if (!this.elements?.copy) {
        return
      }

      const description = this.getDescription()
      this.elements.copy.innerText = description
    },

    /**
     * Set DOM elements.
     */
    setElements() {
      this.elements.container =
        document.querySelector(`[data-section-id=${this.sectionId}]`)

      if (!this.elements.container) {
        return
      }

      for (const selector in this.selectors) {
        this.elements[selector] =
          this.elements.container.querySelector(this.selectors[selector])
      }
    },

    /**
     * Set Event listeners.
     */
    setEventListeners() {
      if (this.elements.readMore) {
        this.elements.readMore.addEventListener('click', this.handleReadMore)
      }

      if (this.elements.linksNext) {
        this.elements.linksNext.addEventListener('click', this.scrollCarousel)

        this.elements.linkList.addEventListener('scroll', (event) => {
          this.handleScrollLinks(event)
        }, { passive: true })
      }
    },

    /**
     * Toggle the visibility state of the description.
     */
    handleReadMore() {
      this.readMoreActive = !this.readMoreActive
    },
  },
}
</script>

<style src="./collection-header.scss" lang="scss"></style>
